import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useChatPopup } from '../../contexts/ChatPopupContext';
import chatService from '../../services/chatService';
import './ChatSidebar.css';

const ChatSidebar = () => {
  const { user, isAuthenticated } = useAuth();
  const { openChatPopup } = useChatPopup();
  const location = useLocation();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Don't show sidebar on chat page
  const isChatPage = location.pathname === '/chat';

  // Load collapsed state from localStorage
  useEffect(() => {
    const savedState = localStorage.getItem('chat-sidebar-collapsed');
    if (savedState !== null) {
      setIsCollapsed(JSON.parse(savedState));
    }
  }, []);

  // Save collapsed state to localStorage
  useEffect(() => {
    localStorage.setItem('chat-sidebar-collapsed', JSON.stringify(isCollapsed));
  }, [isCollapsed]);

  // Load chat rooms
  const loadChatRooms = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      setLoading(true);
      const response = await chatService.getChatRooms();
      if (response.success) {
        setRooms(response.data || []);
      }
    } catch (err) {
      console.error('Error loading chat rooms:', err);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  // Load rooms on mount and when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      loadChatRooms();
    }
  }, [isAuthenticated, loadChatRooms]);

  // Listen for new messages and room updates
  useEffect(() => {
    if (!isAuthenticated) return;

    const handleNewMessage = (event) => {
      const data = event.detail;
      if (data && data.room_id) {
        // Update room's latest message
        setRooms(prevRooms => {
          return prevRooms.map(room => {
            if (room.id === data.room_id) {
              return {
                ...room,
                latest_message: data.message || data,
                updated_at: data.message?.created_at || new Date().toISOString(),
                unread_count: data.message?.user_id !== user?.id 
                  ? (room.unread_count || 0) + 1 
                  : (room.unread_count || 0),
              };
            }
            return room;
          }).sort((a, b) => {
            // Sort by updated_at, most recent first
            const aTime = new Date(a.updated_at || 0);
            const bTime = new Date(b.updated_at || 0);
            return bTime - aTime;
          });
        });
      }
    };

    const handleChatRoomCreated = () => {
      loadChatRooms();
    };

    const handleUnreadCountUpdate = (event) => {
      const data = event.detail;
      if (data && data.unread_counts) {
        setRooms(prevRooms => {
          return prevRooms.map(room => {
            const unreadCount = data.unread_counts[room.id] || 0;
            return {
              ...room,
              unread_count: unreadCount,
            };
          });
        });
      }
    };

    // Listen for user online/offline events
    const handleUserOnline = (event) => {
      const data = event.detail;
      if (data && data.user_id) {
        setRooms(prevRooms => {
          return prevRooms.map(room => {
            const updatedPeers = room.peers?.map(peer => {
              if (peer.id === data.user_id) {
                return {
                  ...peer,
                  is_online: true,
                  last_seen_at: data.last_seen_at,
                };
              }
              return peer;
            });
            
            if (updatedPeers) {
              return {
                ...room,
                peers: updatedPeers,
              };
            }
            return room;
          });
        });
      }
    };

    const handleUserOffline = (event) => {
      const data = event.detail;
      if (data && data.user_id) {
        setRooms(prevRooms => {
          return prevRooms.map(room => {
            const updatedPeers = room.peers?.map(peer => {
              if (peer.id === data.user_id) {
                return {
                  ...peer,
                  is_online: false,
                  last_seen_at: data.last_seen_at,
                };
              }
              return peer;
            });
            
            if (updatedPeers) {
              return {
                ...room,
                peers: updatedPeers,
              };
            }
            return room;
          });
        });
      }
    };

    window.addEventListener('pusher:new-message', handleNewMessage);
    window.addEventListener('pusher:chat-room-created', handleChatRoomCreated);
    window.addEventListener('pusher:chat-unread-count-updated', handleUnreadCountUpdate);
    window.addEventListener('pusher:user-online', handleUserOnline);
    window.addEventListener('pusher:user-offline', handleUserOffline);

    return () => {
      window.removeEventListener('pusher:new-message', handleNewMessage);
      window.removeEventListener('pusher:chat-room-created', handleChatRoomCreated);
      window.removeEventListener('pusher:chat-unread-count-updated', handleUnreadCountUpdate);
      window.removeEventListener('pusher:user-online', handleUserOnline);
      window.removeEventListener('pusher:user-offline', handleUserOffline);
    };
  }, [isAuthenticated, user?.id, loadChatRooms]);

  // Dispatch event when collapsed state changes for popup positioning
  useEffect(() => {
    const sidebarMargin = 20; // Margin from right edge
    const sidebarWidth = 320; // Sidebar width
    const event = new CustomEvent('chat-sidebar-state-changed', {
      detail: { 
        isCollapsed, 
        // Always account for sidebar width to prevent overlap, even when collapsed
        sidebarWidth: sidebarWidth,
        sidebarMargin: sidebarMargin
      }
    });
    window.dispatchEvent(event);
  }, [isCollapsed]);

  const handleChatClick = (room) => {
    // Get the peer user from the room
    const peer = room.peers && room.peers.length > 0 ? room.peers[0] : null;
    if (peer) {
      openChatPopup(
        peer.id,
        peer.name,
        peer.profile_photo,
        room
      );
    }
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m`;
    if (hours < 24) return `${hours}h`;
    if (days < 7) return `${days}d`;
    
    return date.toLocaleDateString();
  };

  const getRoomDisplayName = (room) => {
    if (room.peers && room.peers.length > 0) {
      return room.peers[0].name;
    }
    return room.name || 'Chat';
  };

  const getRoomAvatar = (room) => {
    if (room.peers && room.peers.length > 0) {
      return room.peers[0].profile_photo;
    }
    return null;
  };

  const filteredRooms = rooms.filter(room => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    const roomName = getRoomDisplayName(room).toLowerCase();
    return roomName.includes(query);
  });

  if (!isAuthenticated || isChatPage) {
    return null;
  }

  const totalUnread = rooms.reduce((sum, room) => sum + (room.unread_count || 0), 0);

  return (
    <div className={`chat-floating-sidebar ${isCollapsed ? 'collapsed' : ''}`}>
      {/* Header */}
      <div 
        className="chat-floating-sidebar-header"
        onClick={isCollapsed ? () => setIsCollapsed(false) : undefined}
        style={{ cursor: isCollapsed ? 'pointer' : 'default' }}
      >
        {!isCollapsed ? (
          <>
            <h3>Messages</h3>
            {totalUnread > 0 && (
              <span className="chat-floating-sidebar-unread-badge">{totalUnread}</span>
            )}
          </>
        ) : (
          <>
            <h3>Messages</h3>
            {totalUnread > 0 && (
              <span className="chat-floating-sidebar-unread-badge">{totalUnread}</span>
            )}
          </>
        )}
        <button
          className="chat-floating-sidebar-toggle"
          onClick={(e) => {
            e.stopPropagation();
            setIsCollapsed(!isCollapsed);
          }}
          title={isCollapsed ? 'Expand chat' : 'Collapse chat'}
        >
          {isCollapsed ? (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 15l-6-6-6 6"/>
            </svg>
          ) : (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M6 9l6 6 6-6"/>
            </svg>
          )}
        </button>
      </div>

      {!isCollapsed && (
        <>
          {/* Search */}
          <div className="chat-floating-sidebar-search">
            <input
              type="text"
              placeholder="Search messages..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          {/* Chat List */}
          <div className="chat-floating-sidebar-list">
            {loading ? (
              <div className="chat-floating-sidebar-loading">
                <div className="chat-spinner"></div>
                <span>Loading chats...</span>
              </div>
            ) : filteredRooms.length === 0 ? (
              <div className="chat-floating-sidebar-empty">
                <p>No chats yet</p>
                <span>Start a conversation to see messages here</span>
              </div>
            ) : (
              filteredRooms.map((room) => {
                const peer = room.peers && room.peers.length > 0 ? room.peers[0] : null;
                return (
                  <div
                    key={room.id}
                    className="chat-floating-sidebar-item"
                    onClick={() => handleChatClick(room)}
                  >
                    <div className="chat-floating-sidebar-item-avatar">
                      {getRoomAvatar(room) ? (
                        <img src={getRoomAvatar(room)} alt={getRoomDisplayName(room)} />
                      ) : (
                        <div className="chat-floating-sidebar-avatar-placeholder">
                          {getRoomDisplayName(room).charAt(0).toUpperCase()}
                        </div>
                      )}
                      {peer?.is_online && (
                        <span className="chat-floating-sidebar-online-indicator"></span>
                      )}
                    </div>
                    <div className="chat-floating-sidebar-item-content">
                      <div className="chat-floating-sidebar-item-header">
                        <span className="chat-floating-sidebar-item-name">{getRoomDisplayName(room)}</span>
                        {room.latest_message && (
                          <span className="chat-floating-sidebar-item-time">
                            {formatTime(room.latest_message.created_at)}
                          </span>
                        )}
                      </div>
                      <div className="chat-floating-sidebar-item-preview">
                        {room.latest_message && (
                          <span className="chat-floating-sidebar-item-message">
                            {room.latest_message.user_id === user?.id ? 'You: ' : ''}
                            {room.latest_message.message}
                          </span>
                        )}
                      </div>
                    </div>
                    {room.unread_count > 0 && (
                      <span className="chat-floating-sidebar-item-unread">{room.unread_count}</span>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default ChatSidebar;

